<script>
  import { activeMatch, currentScreen, settings, matchSetup, savedMatches, recoveryInfo, peerRole, recoverableRecordings, recordingSaveState, currentUser, showLoginModal, showPremiumModal, updateAvailable, authInitialized } from '../store.js';
  import { isNativeApp } from '../../platform.js';
  import { PLAY_STORE_URL } from '../../app-config.js';
  import { hasProAccess } from '../../entitlements.js';
  import { checkInbox, markInboxSeen } from '../../services/inbox.js';
  import { estimateRecoveredDurationMs, applyWebmDuration } from '../../recording-utils.js';
  import { saveBlobToDevice, shareSavedFile } from '../../platform-save.js';
  import { appSettings, deleteSavedMatch, saveSettings, persist, restorePreviousCheckpoint, discardRecovery, clearActiveMatch } from '../../store.js';
  import { auth } from '../../firebase-config.js';
  import { buildResumeUrl, buildLiveUrl, qrCodeImageUrl, cloudSyncMatch, cloudResumeMatch, exportMatchToFile, importMatchFromFile, exportAllData, importAllData } from '../../store.js';
  import { cancelPendingInningsEnd } from '../../match-actions.js';
  import { isInningsOver } from '../../scoring-logic.js';
  import { resumeScreenForMatch } from '../../device-session.js';
  import { toast, alertDialog, confirmDialog, promptDialog } from '../../ui/dialogs.js';
  import {
    confirmRecoverableRecordingSaved,
    deleteRecoverableRecording,
    finalizeRecoverableRecording,
    loadRecoverableRecordings
  } from '../../media/recording-safety-service.js';
  import { deleteMatchFromCloud } from '../../services/cloud-sync.js';

  import OnboardingTour from '../components/OnboardingTour.svelte';
  import Icon from '../components/Icon.svelte';

  let scheduledMatches = JSON.parse(localStorage.getItem('cs_scheduled_v1') || "[]");
  let showTour = (() => {
    try { return localStorage.getItem('cs_onboarded_v1') !== '1'; } catch (_) { return false; }
  })();

  // Re-read scheduled matches when the screen mounts (in case we just scheduled one)
  import { onMount } from 'svelte';
  onMount(() => {
    scheduledMatches = JSON.parse(localStorage.getItem('cs_scheduled_v1') || "[]");
  });

  async function startChallengeMatch() {
    if ($activeMatch && $settings.confirmNewMatch !== false && !(await confirmDialog('Discard the active match and start a new one?', { title: 'New match', danger: true }))) return;
    cancelPendingInningsEnd();
    if ($activeMatch?.id) discardRecovery($activeMatch.id);
    await clearActiveMatch($activeMatch?.id);
    currentScreen.set('setup-challenge');
  }

  async function startNewMatch() {
    if ($activeMatch && $settings.confirmNewMatch !== false && !(await confirmDialog('Discard the active match and start a new one?', { title: 'New match', danger: true }))) return;
    cancelPendingInningsEnd();
    if ($activeMatch?.id) discardRecovery($activeMatch.id);
    matchSetup.set(null);
    await clearActiveMatch($activeMatch?.id);
    currentScreen.set('setup');
  }

  function joinRecorder() {
    if (!hasProAccess($currentUser)) {
      showPremiumModal.set(true);
      return;
    }
    currentScreen.set('recorder-join');
  }

  function scheduleMatch() {
    currentScreen.set('schedule');
  }

  function openTournaments() {
    currentScreen.set('tournaments');
  }

  async function startScheduledMatch(match) {
    cancelPendingInningsEnd();
    const setup = {
      teamA: match.teamA,
      teamB: match.teamB,
      overs: match.overs,
      mode: "full", lms: true,
      tossWinner: "A", tossDecision: "bat"
    };
    // Pass pre-entered players if they exist
    if (Array.isArray(match.playersA) && match.playersA.length > 0) {
      setup.playersA = [...match.playersA];
      // Pad to at least 8 slots for the Players screen
      while (setup.playersA.length < 8) setup.playersA.push("");
    }
    if (Array.isArray(match.playersB) && match.playersB.length > 0) {
      setup.playersB = [...match.playersB];
      while (setup.playersB.length < 8) setup.playersB.push("");
    }
    matchSetup.set(setup);
    if (match.venue) {
      settings.update(s => ({ ...s, venue: match.venue }));
      appSettings.venue = match.venue;
      saveSettings();
    }
    await clearActiveMatch($activeMatch?.id);
    currentScreen.set('setup'); // Let them review and do the toss
    // Remove scheduled match after starting it
    const remaining = scheduledMatches.filter(m => m !== match);
    localStorage.setItem('cs_scheduled_v1', JSON.stringify(remaining));
    scheduledMatches = remaining;
  }

  async function deleteScheduled(id) {
    if (await confirmDialog("Delete this scheduled match?", { title: "Delete", danger: true })) {
      const filtered = scheduledMatches.filter(m => m.id !== id);
      localStorage.setItem('cs_scheduled_v1', JSON.stringify(filtered));
      scheduledMatches = filtered;
    }
  }

  function resumeMatch() {
    const m = $activeMatch;
    if (!m) return;
    currentScreen.set(resumeScreenForMatch(m, $peerRole, isInningsOver(m)));
  }

  async function discardMatch() {
    if (await confirmDialog("Discard active match?", { title: "Discard match", danger: true })) {
      cancelPendingInningsEnd();
      discardRecovery($activeMatch?.id);
      await clearActiveMatch($activeMatch?.id);
    }
  }

  async function restorePreviousBall() {
    if (!$activeMatch || !(await confirmDialog('Restore the previous safe ball? Current latest state will be replaced.', { title: 'Restore ball' }))) return;
    if (!(await restorePreviousCheckpoint($activeMatch.id))) toast('No previous safe ball is available.', 'error');
  }

  async function handleCloudSync() {
    if (!$activeMatch) return;
    try {
      const code = await cloudSyncMatch($activeMatch);
      if (code) {
        toast("✅ Match synced! Code: " + code + " — latest code stays on Home until next sync.", "success", 4500);
        // App settings are reactive
        settings.update(s => ({ ...s, _lastSyncCode: code, _lastSyncAt: Date.now() }));
      } else {
        toast("Sync code nahi bana — internet check karo. (Logged-in account backup alag se chalta rehta hai.)", "error", 4500);
      }
    } catch (err) {
      toast("Network error. Use Export to File instead.", "error");
    }
  }

  async function handleCloudResume() {
    const code = await promptDialog("Enter sync code from other phone:", { title: "Cloud Restore", placeholder: "e.g. AB12CD" });
    if (!code) return;
    try {
      const data = await cloudResumeMatch(code.trim());
      if (data && data.id) {
        activeMatch.set(data);
        persist();
        toast("✅ Match resumed!", "success");
      } else {
        toast("Invalid code or sync expired.", "error");
      }
    } catch (err) {
      toast("Network error or invalid code.", "error");
    }
  }

  function openSettingsModal() {
    currentScreen.set('settings');
  }

  async function deleteMatch(mId) {
    if (await confirmDialog("Delete this match?", { title: "Delete match", danger: true })) {
      const newSaved = $savedMatches.filter(x => x.id !== mId);
      savedMatches.set(newSaved);
      // Targeted delete instead of clear-and-rewrite-everything.
      await deleteSavedMatch(mId);
      // Try to delete from cloud if user is logged in
      try {
        await deleteMatchFromCloud(mId);
      } catch (e) {
        console.error("Cloud delete failed:", e);
      }
    }
  }
  async function openSavedMatch(m) {
    if ($activeMatch && $activeMatch.id !== m.id) {
      if (!(await confirmDialog("This will overwrite your current active match. Continue?", { title: "Open match", danger: true }))) return;
    }
    activeMatch.set(m);
    persist();
    if (!m.result) {
      resumeMatch();
    } else {
      currentScreen.set('result');
    }
  }

  async function saveInterruptedRecording(recording) {
    recordingSaveState.set({ status: 'finalizing', message: 'Preparing recovered recording…', fileName: recording.fileName || '', bytes: recording.bytes || 0, recordingId: recording.recordingId });
    try {
      const finished = await finalizeRecoverableRecording(recording.recordingId);
      if (!finished?.blob?.size) throw new Error('Recovery file is empty');
      // Crash-recovered WebM files have no duration header — estimate it from
      // the chunk timeline so players don't show 0:00/∞ and seeking works.
      const durationMs = estimateRecoveredDurationMs(finished.manifest || recording);
      const blob = await applyWebmDuration(finished.blob, durationMs);
      const fileName = recording.fileName || `Recovered_Recording_${recording.recordingId}.webm`;
      // saveBlobToDevice handles BOTH platforms — a raw <a download> anchor
      // silently does nothing inside the Capacitor WebView (recovered videos
      // were unsavable in the Android app).
      const result = await saveBlobToDevice(blob, fileName, {
        onProgress: f => recordingSaveState.set({ status: 'finalizing', message: `Saving recovered video… ${Math.round(f * 100)}%`, fileName, bytes: blob.size, recordingId: recording.recordingId })
      });
      if (!result.ok) throw result.error || new Error('Recovered video could not be saved.');
      if (result.uri) await shareSavedFile(result.uri, fileName);
      recordingSaveState.set({ status: 'download-started', message: 'Video saved — share or move it, then confirm.', fileName, bytes: blob.size, recordingId: recording.recordingId });
    } catch (error) {
      recordingSaveState.set({ status: 'error', message: error?.message || 'Recovered recording could not be prepared.', fileName: recording.fileName || '', bytes: recording.bytes || 0, recordingId: recording.recordingId });
    }
  }

  async function confirmRecoveredFile(recordingId) {
    await confirmRecoverableRecordingSaved(recordingId);
    recordingSaveState.set({ status: 'confirmed', message: 'Saved successfully', fileName: '', bytes: 0, recordingId: null });
  }

  async function removeInterruptedRecording(recording) {
    if (!(await confirmDialog('Delete this interrupted recording permanently?', { title: 'Delete recording', danger: true }))) return;
    await deleteRecoverableRecording(recording.recordingId);
    await loadRecoverableRecordings();
  }
  let activeTab = 'matches'; // 'matches', 'upcoming', 'transfer'

  // In-app inbox — "Tigers ne aapko add kiya" style alerts (Spark-safe, no FCM)
  let inboxItems = [];
  $: if ($currentUser?.uid && $authInitialized) { refreshInbox(); }
  let _inboxChecked = false;
  async function refreshInbox() {
    if (_inboxChecked) return;
    _inboxChecked = true;
    try { inboxItems = await checkInbox(); } catch (_) {}
  }
  function dismissInboxItem(item) {
    markInboxSeen([item]);
    inboxItems = inboxItems.filter(n => n.id !== item.id);
  }

  // Soft update banner (non-blocking; dismissible per app session)
  let updateBannerDismissed = false;
  function applySoftUpdate() {
    if (isNativeApp) {
      window.location.href = PLAY_STORE_URL;
    } else {
      window.location.reload();
    }
  }
</script>

{#if showTour}
  <OnboardingTour on:close={() => showTour = false} />
{/if}

<div class="screen" id="home-screen">
  <!-- HERO APP BAR -->
  <div class="glass-card hero-header-bar" style="padding: 14px 16px; margin-bottom: 14px; display: flex; justify-content: space-between; align-items: center; border-radius: 18px;">
    <div style="display:flex; align-items:center; gap:10px;">
      <div class="logo-glow-wrapper">
        <img src="/criccam_pro_logo.png" alt="CricCam Pro" style="height:38px; width:38px; border-radius:10px; object-fit:cover;" />
      </div>
      <div>
        <h1 style="margin:0; font-size:22px; font-weight:900; letter-spacing:-0.5px; color:#fff; display:flex; align-items:center; gap:6px;">
          CricCam <span style="background: linear-gradient(135deg, #ef4444 0%, #f59e0b 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-style:italic; font-weight:900;">PRO</span>
          {#if $activeMatch}
            <span class="live-badge-pulse">● LIVE</span>
          {/if}
        </h1>
        <p class="muted" style="margin: 0; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; color: #94a3b8;">TV BROADCAST &amp; DUAL-PHONE STUDIO</p>
      </div>
    </div>
    
    <div style="display:flex; gap: 8px; align-items:center">
      {#if $currentUser}
        {#if hasProAccess($currentUser)}
          <span style="background: linear-gradient(135deg, #fbbf24 0%, #d97706 100%); color: #451a03; padding: 5px 10px; font-size: 11px; font-weight: 900; border-radius: 8px; text-transform: uppercase; box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);">PRO</span>
        {:else}
          <button class="btn btn-haptic" style="padding:6px 12px; font-size:11px; background: linear-gradient(135deg, #fbbf24, #f59e0b); color: #451a03; font-weight: 900; border: none; border-radius:8px;" on:click={() => showPremiumModal.set(true)}>
            ⭐ UPGRADE
          </button>
        {/if}
        <button class="btn btn-haptic glass-card" style="padding:6px 10px; font-size:12px; border-radius:8px; max-width:110px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" on:click={() => currentScreen.set('profile')} title="View Profile">
          👤 { ($currentUser.displayName || $currentUser.phoneNumber || $currentUser.email || 'User').split('@')[0] }
        </button>
      {:else}
        <button class="btn btn-haptic" style="padding:6px 12px; font-size:12px; border:1px solid #10b981; color: #10b981; background: rgba(16,185,129,0.1); border-radius:8px; display:flex; align-items:center; gap:5px; font-weight:800;" on:click={() => showLoginModal.set(true)}>
          <Icon name="lock" size={13} /> Login
        </button>
      {/if}
      <button class="btn btn-haptic glass-card" style="padding:8px; border-radius:8px; display:flex; align-items:center;" on:click={openSettingsModal} title="Settings" aria-label="Settings"><Icon name="settings" size={18} /></button>
    </div>
  </div>

  {#if $updateAvailable && !updateBannerDismissed}
    <div class="glass-card" style="display:flex; align-items:center; gap:10px; background: linear-gradient(135deg, rgba(6,78,59,0.9), rgba(15,23,42,0.95)); border: 1px solid #10b981; color:#d1fae5; font-size:12px; font-weight:700; padding:12px 14px; margin-bottom: 12px;">
      <span style="font-size:20px;">🚀</span>
      <span style="flex:1;">New version available — update to unlock latest features!</span>
      <button class="btn btn-haptic" style="padding:6px 14px; font-size:12px; background:#10b981; border:none; font-weight:900; border-radius:8px; color:#fff;" on:click={applySoftUpdate}>Update</button>
      <button class="btn btn-ghost" style="padding:6px 10px; font-size:12px;" on:click={() => updateBannerDismissed = true}>Later</button>
    </div>
  {/if}

  {#each inboxItems as item (item.id)}
    <div class="glass-card" style="display:flex; align-items:flex-start; gap:10px; background:{item.type === 'team' ? 'rgba(20,83,45,0.85)' : 'rgba(30,58,138,0.85)'}; border-color:{item.type === 'team' ? '#22c55e' : '#3b82f6'}; padding:12px; margin-bottom: 12px;">
      <div style="flex:1; min-width:0;">
        <div style="font-size:13px; font-weight:900; color:#fff;">{item.title}</div>
        <div class="muted" style="font-size:11px; margin-top:3px; line-height:1.4;">{item.body}</div>
      </div>
      <button class="btn btn-haptic" style="padding:4px 10px; font-size:11px; background:rgba(255,255,255,0.15); border-radius:6px; flex-shrink:0;" on:click={() => dismissInboxItem(item)}>OK</button>
    </div>
  {/each}

  {#if $recoveryInfo.warning && !$activeMatch}
    <div class="glass-card" style="background:rgba(120,53,15,0.85); border-color:#f59e0b; color:#fef3c7; font-size:12px; font-weight:700; padding:12px; margin-bottom: 12px;">
      {$recoveryInfo.warning}
    </div>
  {/if}

  <!-- MAIN HIGH-AESTHETIC ACTION CARDS -->
  <div style="display:grid; grid-template-columns: repeat(2, 1fr); gap:10px; margin-bottom: 14px;">
    <button class="btn btn-haptic main-action-card card-blue" on:click={startNewMatch}>
      <div class="action-icon-circle"><Icon name="play" size={20} /></div>
      <div class="action-card-text">
        <span class="title">NEW MATCH</span>
        <span class="sub">Standard Scoring</span>
      </div>
    </button>

    <button class="btn btn-haptic main-action-card card-amber" on:click={startChallengeMatch}>
      <div class="action-icon-circle"><Icon name="zap" size={20} /></div>
      <div class="action-card-text">
        <span class="title">CHALLENGE</span>
        <span class="sub">Custom Target Mode</span>
      </div>
    </button>

    <button class="btn btn-haptic main-action-card card-indigo" on:click={() => currentScreen.set('teams')}>
      <div class="action-icon-circle"><Icon name="users" size={20} /></div>
      <div class="action-card-text">
        <span class="title">TEAMS</span>
        <span class="sub">Rosters &amp; Squads</span>
      </div>
    </button>

    <button class="btn btn-haptic main-action-card card-emerald" on:click={() => currentScreen.set('spectator')}>
      <div class="action-icon-circle"><Icon name="tv" size={20} /></div>
      <div class="action-card-text">
        <span class="title">SPECTATE LIVE</span>
        <span class="sub">Broadcast Stream 🌐</span>
      </div>
    </button>
  </div>

  <!-- QUICK FEATURE HUBS -->
  <div style="display:grid; grid-template-columns: repeat(3, 1fr); gap:8px; margin-bottom:18px;">
    <button class="btn btn-haptic glass-card quick-hub-btn" on:click={openTournaments}>
      <span class="hub-icon trophy-color"><Icon name="trophy" size={22} /></span>
      <span class="hub-label">Tournaments</span>
    </button>
    <button class="btn btn-haptic glass-card quick-hub-btn" on:click={() => currentScreen.set('teams')}>
      <span class="hub-icon shield-color"><Icon name="shield" size={22} /></span>
      <span class="hub-label">Teams</span>
    </button>
    <button class="btn btn-haptic glass-card quick-hub-btn" on:click={joinRecorder}>
      <span class="hub-icon video-color"><Icon name="video" size={22} /></span>
      <span class="hub-label">Join Cam</span>
    </button>
  </div>

  <!-- ACTIVE MATCH RESUME HERO CARD -->
  {#if $activeMatch}
    <div class="glass-card active-match-hero-card" style="margin-bottom: 18px;">
      {#if $recoveryInfo.warning}
        <div style="padding:8px 12px; margin-bottom:10px; border-radius:8px; background:rgba(180,83,9,0.5); color:#fef3c7; font-size:11px; font-weight:700;">{$recoveryInfo.warning}</div>
      {/if}
      <div style="display:flex; justify-content:space-between; align-items:center">
        <div>
          <div class="live-pulse-heading">
            <span class="pulse-dot"></span>
            MATCH IN PROGRESS
          </div>
          <div style="font-size:16px; font-weight:900; color:#fff; margin-top:2px;">
            {$activeMatch.teamA.name} <span style="color:#94a3b8; font-weight:500;">vs</span> {$activeMatch.teamB.name}
          </div>
        </div>
        <button class="btn btn-haptic" style="background: linear-gradient(135deg, #fbbf24, #f59e0b); color:#451a03; border:none; font-weight:900; font-size:13px; padding:10px 18px; border-radius:12px; box-shadow: 0 4px 15px rgba(251,191,36,0.35);" on:click={resumeMatch}>
          RESUME ▶
        </button>
      </div>

      {#if $recoveryInfo.latest?.summary}
        <div class="meta tabular-nums" style="margin-top:10px; font-size:12px; color:#cbd5e1; font-weight:700;">
          📊 Safe score: {$recoveryInfo.latest.summary.score} · {$recoveryInfo.latest.summary.balls} balls
        </div>
      {/if}

      <div style="display:flex; gap:8px; margin-top:12px;">
        <button class="btn btn-haptic glass-card" style="flex:1; font-size:11px; padding:8px; font-weight:700;" disabled={!$recoveryInfo.previous} on:click={restorePreviousBall}>Restore Ball</button>
        <button class="btn btn-haptic glass-card" style="flex:1; font-size:11px; padding:8px; color:#f87171; border-color:rgba(239,68,68,0.3); font-weight:700;" on:click={discardMatch}>Discard</button>
      </div>
    </div>
  {/if}

  <!-- TAB NAVIGATION -->
  <div class="glass-card tab-bar-container" style="padding: 4px; border-radius: 14px; margin-bottom: 16px;">
    <button class="tab-btn {activeTab === 'matches' ? 'active' : ''}" on:click={() => activeTab = 'matches'}>
      <Icon name="save" size={13} /> Matches
    </button>
    <button class="tab-btn {activeTab === 'upcoming' ? 'active' : ''}" on:click={() => activeTab = 'upcoming'}>
      <Icon name="calendar" size={13} /> Upcoming ({scheduledMatches.length})
    </button>
    <button class="tab-btn {activeTab === 'transfer' ? 'active' : ''}" on:click={() => activeTab = 'transfer'}>
      <Icon name="database" size={13} /> Backup
    </button>
  </div>


  <!-- TAB CONTENT -->
  {#if activeTab === 'matches'}
    <!-- SAVED MATCHES -->
    {#if $savedMatches.length === 0}
      <div class="muted" style="padding:40px 20px; text-align:center; border:1px dashed #1e293b; border-radius:10px; font-size:13px;">No saved matches yet. Start scoring to see history.</div>
    {:else}
      {#each $savedMatches.slice().reverse() as m, i_each (m.id || m.name || i_each)}
        <div class="match-card" style="cursor:pointer; display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; padding:12px; background:#1e293b; border-radius:8px;" on:click={() => openSavedMatch(m)} on:keydown={(e) => e.key === 'Enter' && openSavedMatch(m)} tabindex="0" role="button">
          <div style="flex:1; text-align:left;">
            <div style="font-weight:800; font-size:14px; color:#fff;">{m.teamA.name} vs {m.teamB.name}</div>
            <div class="meta" style="font-size:11px; margin-top:2px;">
              {new Date(m.date).toLocaleDateString()} · {m.overs} ov ·
              {!m.result ? "Incomplete"
                : m.result.winner === "TIE" ? "Match tied"
                : m.result.winner === "COMPLETED" ? "Completed"
                : `${m.result.winner === "A" ? m.teamA.name : m.teamB.name} won by ${m.result.margin} ${m.result.marginType}`}
            </div>
          </div>
          <button class="btn btn-ghost" style="padding:6px; border:none; color:#f87171;" aria-label="Delete match" on:click|stopPropagation={() => deleteMatch(m.id)}><Icon name="trash" size={15} /></button>
        </div>
      {/each}
    {/if}

  {:else if activeTab === 'upcoming'}
    <!-- UPCOMING/SCHEDULED MATCHES -->
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
      <h3 style="margin:0; font-size:14px; color:#fff;">Scheduled Matches</h3>
      <button class="btn btn-primary" style="padding:4px 8px; font-size:11px;" on:click={scheduleMatch}>+ Schedule</button>
    </div>
    {#if scheduledMatches.length === 0}
      <div class="muted" style="padding:40px 20px; text-align:center; border:1px dashed #1e293b; border-radius:10px; font-size:13px;">No scheduled matches. Click "+ Schedule" to plan future games.</div>
    {:else}
      {#each scheduledMatches as m, i_each (m.id || m.name || i_each)}
        <div class="card" style="margin-bottom:8px; display:flex; justify-content:space-between; align-items:center; padding:12px;">
          <div style="text-align:left;">
            <div style="font-weight:800; color:#fbbf24; font-size:14px;">{m.teamA.name} vs {m.teamB.name}</div>
            <div class="meta" style="font-size:11px; margin-top:2px;">{m.date} at {m.time} · {m.venue} · {m.overs} overs</div>
          </div>
          <div style="display:flex; gap:6px;">
            <button class="btn btn-primary" style="padding:4px 10px; font-size:11px; font-weight:800;" on:click={() => startScheduledMatch(m)}>START</button>
            <button class="btn btn-ghost" style="padding:4px 8px; font-size:11px; color:#f87171;" aria-label="Delete scheduled match" on:click={() => deleteScheduled(m.id)}><Icon name="trash" size={13} /></button>
          </div>
        </div>
      {/each}
    {/if}

  {:else if activeTab === 'transfer'}
    <!-- BACKUP & PHONE TO PHONE TRANSFER -->
    <h3 style="margin:0 0 10px 0; font-size:14px; color:#fff; text-align:left;">Device Synchronization</h3>
    
    {#if $activeMatch && $settings._lastSyncCode}
      <div class="card" style="margin-bottom:12px; padding:12px; background:#063116; border:1px solid #166534">
        <div style="font-size:10px; color:#4ade80; margin-bottom:6px; font-weight:700; letter-spacing:0.5px">✓ LATEST CLOUD SYNC</div>
        <div style="display:flex; gap:12px; align-items:flex-start">
          {#await qrCodeImageUrl(buildResumeUrl($settings._lastSyncCode), 100) then qrUrl}
            {#if qrUrl}
              <img
                src={qrUrl}
                alt="QR Code"
                width="100" height="100"
                style="background:#fff; border-radius:4px; padding:3px; flex-shrink:0" />
            {/if}
          {/await}
          <div style="flex:1; min-width:0; text-align:left;">
            <div class="muted" style="font-size:10px;">Sync Code:</div>
            <div style="font-family:monospace; font-size:13px; font-weight:800; color:#fff; margin-bottom:6px;">{$settings._lastSyncCode}</div>
            <button class="btn btn-ghost" style="width:100%; padding:4px 8px; font-size:10px; margin-bottom:2px" on:click={async (e) => {
              const resumeUrl = buildResumeUrl($settings._lastSyncCode);
              try {
                await navigator.clipboard.writeText(resumeUrl);
                e.target.innerText = "✓ Copied";
                setTimeout(() => e.target.innerText = "📋 Copy Link", 1500);
              } catch (_) {}
            }}>📋 Copy Link</button>
            <button class="btn btn-ghost" style="width:100%; padding:4px 8px; font-size:10px; border-color:#166534; color:#4ade80;" on:click={async (e) => {
              const liveUrl = buildLiveUrl($settings._lastSyncCode);
              if (!liveUrl) { toast('Live link needs the app deployed online.', 'error'); return; }
              try {
                if (navigator.share) {
                  await navigator.share({ title: 'Live Cricket Score', text: '🏏 Match LIVE dekho:', url: liveUrl });
                } else {
                  await navigator.clipboard.writeText(liveUrl);
                  e.target.innerText = '✓ Live Link Copied';
                  setTimeout(() => e.target.innerText = '📺 Share Live Link', 1500);
                }
              } catch (_) {}
            }}>📺 Share Live Link</button>
            <div class="muted" style="font-size:9px; margin-top:3px; line-height:1.4;">Live link se koi bhi bina app ke live score dekh sakta hai — har ball ke baad auto-update.</div>
          </div>
        </div>
      </div>
    {/if}

    <div style="display:flex; flex-direction:column; gap:8px;">
      <div style="display:flex; gap:8px;">
        <button class="btn btn-primary" style="flex:1; font-size:12px; padding:8px;" disabled={!$activeMatch} on:click={handleCloudSync}>
          ⬆ Cloud Backup
        </button>
        <button class="btn btn-ghost" style="flex:1; font-size:12px; padding:8px;" on:click={handleCloudResume}>
          ⬇ Cloud Restore
        </button>
      </div>

      <div style="display:flex; gap:8px;">
        <button class="btn btn-primary" style="flex:1; font-size:12px; padding:8px;" disabled={!$activeMatch} on:click={() => exportMatchToFile($activeMatch)}>
          💾 Export File
        </button>
        <button class="btn btn-ghost" style="flex:1; font-size:12px; padding:8px;" on:click={importMatchFromFile}>
          📂 Import File
        </button>
      </div>

      <div style="display:flex; gap:8px;">
        <button class="btn btn-primary" style="flex:1; font-size:12px; padding:8px;" disabled={!$activeMatch && scheduledMatches.length === 0} on:click={() => exportAllData($activeMatch, scheduledMatches)}>
          📦 Export All
        </button>
        <button class="btn btn-ghost" style="flex:1; font-size:12px; padding:8px;" on:click={() => importAllData((updated) => { if (updated.scheduled) scheduledMatches = JSON.parse(localStorage.getItem('cs_scheduled_v1') || '[]'); })}>
          📦 Import All
        </button>
      </div>
    </div>

    <!-- INTERRUPTED RECORDINGS RECOVERY -->
    {#if $recoverableRecordings.length > 0}
      <h3 style="margin:16px 0 8px 0; font-size:14px; color:#ef4444; text-align:left;">⚠️ Interrupted Recordings</h3>
      {#each $recoverableRecordings as recording, i_each (recording.id || recording.name || i_each)}
        <div class="card" style="margin-bottom:8px; padding:10px; display:flex; justify-content:space-between; align-items:center;">
          <div style="text-align:left;">
            <div style="font-weight:800; font-size:13px; color:#fff;">{recording.teams || 'Cricket Video'}</div>
            <div class="meta" style="font-size:10px;">{Math.max(1, Math.round((recording.bytes || 0) / 1024 / 1024))} MB · {new Date(recording.createdAt).toLocaleDateString()}</div>
          </div>
          <div style="display:flex; flex-direction:column; gap:4px;">
            <button class="btn btn-primary" style="padding:4px 8px; font-size:11px;" on:click={() => saveInterruptedRecording(recording)}>Save Recording</button>
            <button class="btn btn-ghost" style="padding:4px 8px; font-size:11px;" on:click={() => removeInterruptedRecording(recording)}>Delete</button>
          </div>
        </div>
      {/each}
    {/if}
  {/if}

  <p class="muted" style="text-align:center; font-size:11px; margin-top:24px; opacity:0.75;">CricCam Pro</p>
</div>

<!-- RESUME EXACTLY RESTORE PREVIOUS BALL DISCARD -->

<style>
  .logo-glow-wrapper {
    position: relative;
    border-radius: 10px;
    box-shadow: 0 0 15px rgba(239, 68, 68, 0.45);
  }

  .live-badge-pulse {
    font-size: 10px;
    font-weight: 900;
    color: #10b981;
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.4);
    padding: 2px 8px;
    border-radius: 999px;
    letter-spacing: 0.5px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    animation: livePulse 1.5s ease-in-out infinite alternate;
  }

  @keyframes livePulse {
    from { opacity: 0.75; box-shadow: 0 0 4px rgba(16, 185, 129, 0.2); }
    to { opacity: 1; box-shadow: 0 0 12px rgba(16, 185, 129, 0.6); }
  }

  /* ── Main Action Cards ────────────────────────────────────────── */
  .main-action-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 14px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #ffffff;
    text-align: left;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    transition: transform 0.15s cubic-bezier(0.32, 0.72, 0, 1), box-shadow 0.15s ease;
  }

  .card-blue { background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); }
  .card-amber { background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%); }
  .card-indigo { background: linear-gradient(135deg, #3730a3 0%, #6366f1 100%); }
  .card-emerald { background: linear-gradient(135deg, #065f46 0%, #10b981 100%); }

  .action-icon-circle {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #ffffff;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  .action-card-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    overflow: hidden;
  }

  .action-card-text .title {
    font-size: 13px;
    font-weight: 900;
    letter-spacing: 0.5px;
    color: #ffffff;
    line-height: 1.1;
  }

  .action-card-text .sub {
    font-size: 10px;
    opacity: 0.85;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Quick Hub Buttons ────────────────────────────────────────── */
  .quick-hub-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 14px 8px;
    border-radius: 16px;
    text-align: center;
    background: rgba(15, 23, 42, 0.7);
  }

  .hub-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .trophy-color { color: #f59e0b; filter: drop-shadow(0 2px 8px rgba(245, 158, 11, 0.4)); }
  .shield-color { color: #10b981; filter: drop-shadow(0 2px 8px rgba(16, 185, 129, 0.4)); }
  .video-color { color: #3b82f6; filter: drop-shadow(0 2px 8px rgba(59, 130, 246, 0.4)); }

  .hub-label {
    font-size: 11px;
    font-weight: 800;
    color: #f1f5f9;
  }

  /* ── Active Match Hero Card ────────────────────────────────────── */
  .active-match-hero-card {
    padding: 16px;
    border-radius: 20px;
    border: 1px solid rgba(251, 191, 36, 0.4);
    background: linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%);
    box-shadow: 0 8px 32px rgba(251, 191, 36, 0.15);
  }

  .live-pulse-heading {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 900;
    color: #fbbf24;
    letter-spacing: 0.8px;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 0 8px #fbbf24;
    animation: livePulse 1s ease-in-out infinite alternate;
  }

  /* ── Tab Switcher ────────────────────────────────────────────── */
  .tab-bar-container {
    display: flex;
    background: rgba(15, 23, 42, 0.8);
    gap: 4px;
  }

  .tab-bar-container .tab-btn {
    flex: 1;
    padding: 10px 0;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 800;
    color: #94a3b8;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.15s ease;
  }

  .tab-bar-container .tab-btn.active {
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
  }
</style>
